import { Category, Video } from '@app/models/_index';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { GoogleAnalyticsService } from '@app/services/_index';
import { HasAnalytics } from '@app/interfaces/analytics.interface';
import { PagingComponent } from '../shared/paging/paging.component';
import { Subscription } from 'rxjs';
import { VideoAdapter } from '@app/adapters/video.adapter';
import { VideoInterface } from '@app/interfaces/video.interface';
import { WordpressVideosService } from '@app/services/wordpress/wordpress-videos.service';
import { logGoogleAnalytics } from '@app/utilities/console-log.utilities';
import { scrollIntoView } from '@app/utilities/_index';

// NOTE: have to import the shared paging using this path instead of the barrel file _index.ts due to compile issues.
@Component({
  selector: 'app-recommended-videos',
  templateUrl: './recommended-videos.component.html',
  styleUrls: ['./recommended-videos.component.scss'],
  standalone: false,
})
export class RecommendedVideosComponent
  extends PagingComponent
  implements OnInit, OnDestroy, HasAnalytics
{
  isLoading: boolean;
  featuredVideos: Video[] = [];
  recommendedVideos: Video[] = [];
  videoCategories: Category[] = [];
  filteredVideos: Video[] = [];
  recommendedVideostemp: Video[] = [];

  subs: Subscription[] = [];
  selectedCategory: string; // Can be a string or number

  constructor(
    private videoAdapter: VideoAdapter,
    private googleAnalyticsService: GoogleAnalyticsService,
    private videoService: WordpressVideosService
  ) {
    super();
  }

  /**
   * Send the query search label to Google Analytics
   * @param category - the source of the google analytics category string (ex. the page or component triggering the action)
   * @param action - the action to be (ex. click)
   * @param dataLabel - the label associated with the action
   */
  sendGoogleAnalytics(category: string, action: string, dataLabel: string): void {
    logGoogleAnalytics(category, action, dataLabel);
    this.googleAnalyticsService.sendEvent(category, action, dataLabel);
  }

  /**
   * OnInit setting page title and metadata
   * Set featured videos, recommended videos and video categories needed for the page.
   */
  ngOnInit(): void {
    this.getWpFeaturedVideos();
    this.getWpRecommendedVideos();
    this.getWpVideoCategories();

    // Set Default Paging parameters
    this.currentPage = 1;
    this.itemsPerPage = 6;
  }

  /**
   * Setting the videos based on the selection of the dropdown
   * @param videoCategory - selectedCategory can be a string for a specific source or a number ID from another source
   * @returns - Video collection of the selectedCategory
   */
  onSetVideoType(videoCategory: string): Video[] {
    this.selectedCategory = videoCategory;

    // NOTE: Allow == type coercion
    // eslint-disable-next-line
    if (!this.selectedCategory || this.selectedCategory == 'recommended') {
      // NOTE: If the selected video category is "recommended", return the reloaded recommended videos.
      this.recommendedVideos = [];
      this.getWpRecommendedVideos();
      return this.recommendedVideos;
    }
    // NOTE: Allow == type coercion
    // eslint-disable-next-line
    else if (!this.selectedCategory || this.selectedCategory == 'featured') {
      // NOTE: If the selected video category is "featured", return the preloaded featured videos.
      this.featuredVideos = [];
      this.getWpFeaturedVideos();
      this.recommendedVideos = this.featuredVideos;
      return this.recommendedVideos;
    } else {
      this.getVideosByWpSelectedCategory();
    }
  }

  /**
   * Getting the video categories needed for the page.
   * Return Video Categories and subscribe to the video service
   */
  getWpVideoCategories(): void {
    this.isLoading = true;
    this.subs.push(
      this.videoService.getWpVideoCategories().subscribe({
        next: (categories: Category[]) => {
          this.videoCategories = categories;
          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
          console.log(error);
        },
        complete: () => {
          // Force is loading to stop and complete in case of any unhandled errors
          this.isLoading = false;
          console.log('Video Categories content completed');
        },
      })
    );
  }

  /**
   * Return WordPress Recommended Videos
   */
  getWpRecommendedVideos(): void {
    this.isLoading = true;

    this.subs.push(
      this.videoService.getWpRecommendedVideos().subscribe({
        next: (videos: VideoInterface[]) => {
          videos.forEach((item: VideoInterface) => {
            const video = this.videoAdapter.adapt(item);
            const videoCategories = video.categories;
            const isFeaturedVideo = videoCategories.some(
              (i) => i.slug && i.slug.includes('featured')
            );
            if (!isFeaturedVideo) {
              this.recommendedVideos.push(video);
            }
          });
          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
          console.log(error);
        },
        complete: () => {
          // Force is loading to stop and complete in case of any unhandled errors
          this.isLoading = false;
          console.log('Recommended Videos content completed');
        },
      })
    );
  }

  /**
   * Load Featured Videos from Wordpress
   * TODO: Refactor this to combine with recommended video with this duplicate code.
   */
  getWpFeaturedVideos(): void {
    this.isLoading = true;
    this.subs.push(
      this.videoService.getWpFeaturedVideos().subscribe({
        next: (videos: VideoInterface[]) => {
          videos.forEach((item: VideoInterface) => {
            const video = this.videoAdapter.adapt(item);
            const videoCategories = video.categories;
            const isFeaturedVideo = videoCategories.some(
              (i) => i.slug && i.slug.includes('featured')
            );
            if (isFeaturedVideo) {
              this.featuredVideos.push(video);
            }
          });
          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
          console.log(error);
        },
        complete: () => {
          // Force is loading to stop and complete in case of any unhandled errors
          this.isLoading = false;
          console.log('Featured Videos content completed');
        },
      })
    );
  }

  /**
   * Getting the videos based on category selection of dropdown.
   * Return Video by Slug and subscribe to the video service
   */
  getVideosByWpSelectedCategory(): void {
    this.isLoading = true;
    const categoryId = Number(this.selectedCategory);
    this.subs.push(
      this.videoService.getWpVideosBySlugId(categoryId).subscribe({
        next: (filteredVideos: VideoInterface[]) => {
          filteredVideos.forEach((filteredVideo: VideoInterface) => {
            this.filteredVideos.push(this.videoAdapter.adapt(filteredVideo));
          });
          this.recommendedVideos = this.filteredVideos;
          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
          console.log(error);
        },
        complete: () => {
          // Force is loading to stop and complete in case of any unhandled errors
          this.isLoading = false;
          console.log('Selected Video content completed');
        },
      })
    );
    this.filteredVideos.length = 0;
  }

  /**
   * Update current selected category page and update
   * vidoe results.  Also scroll into view after page change.
   * @param page - set current page to selected page
   */
  pageChanged(page: number): void {
    if (this.hasPageChanged(page)) {
      this.currentPage = page;
    }
    scrollIntoView('contentWrapper', 'start');
  }

  /**
   * On Destroy, unsubscribe subscriptions to prevent memory leaks
   */
  ngOnDestroy(): void {
    if (this.subs) {
      this.subs.forEach((sub) => {
        sub.unsubscribe();
      });
    }
  }

  /**
   * Add Track By Category Id to not redraw or re-render list items that have not changed.
   * This improves performance.
   * @param index - Index of Video Category array
   * @param item - Category item
   * @returns Category id
   */
  trackByCategoryId(index: number, item: Category): number {
    if (item) {
      return item.id;
    } else {
      return index;
    }
  }

  /**
   * Add Track By Recommended Video Id to not redraw or re-render list items that have not changed.
   * This improves performance.
   * @param index - Index of Recommended Video array
   * @param item - Recommended Video item
   * @returns Recommended Video id
   */
  trackByRecommendedVideoId(index: number, item: Video): string {
    if (item) {
      return item.id;
    } else {
      return String(index);
    }
  }
}
